import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Eye, EyeOff, Key } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AddCredential({ user }) {
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [categories, setCategories] = useState([]);
  const [projects, setProjects] = useState([]);
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    title: '',
    username: '',
    password: '',
    url: '',
    category: 'Other',
    notes: '',
    expiry_date: '',
    project_id: ''
  });

  useEffect(() => {
    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };
    axios.get(`${API}/categories`, { headers }).then(res => setCategories(res.data)).catch(() => {});
    axios.get(`${API}/projects`, { headers }).then(res => setProjects(res.data)).catch(() => {});
  }, []);

  const generatePassword = () => {
    const length = 16;
    const charset = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*';
    let password = '';
    for (let i = 0; i < length; i++) {
      password += charset.charAt(Math.floor(Math.random() * charset.length));
    }
    setFormData({ ...formData, password });
    toast.success('Secure password generated!');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/credentials`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Credential added successfully!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add credential');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-8">
      <button
        onClick={() => navigate('/dashboard')}
        className="flex items-center gap-2 text-text-secondary hover:text-text-primary mb-6 font-medium"
        data-testid="btn-back-to-credentials"
      >
        <ArrowLeft size={20} />
        Back to Credentials
      </button>

      <div className="max-w-2xl">
        <h1 className="font-manrope font-bold text-4xl text-text-primary tracking-tight mb-2">
          Add Credential
        </h1>
        <p className="text-text-secondary mb-8">
          All data will be encrypted before storage
        </p>

        <div className="bg-white border-2 border-gray-200 p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                Title *
              </label>
              <input
                type="text"
                required
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="w-full bg-white border-2 border-gray-200 p-3 focus:border-primary focus:outline-none text-text-primary"
                placeholder="e.g., Production Database"
                data-testid="input-credential-title"
              />
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">
                  Username *
                </label>
                <input
                  type="text"
                  required
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  className="w-full bg-white border-2 border-gray-200 p-3 focus:border-primary focus:outline-none text-text-primary"
                  placeholder="admin@example.com"
                  autoComplete="off"
                  data-testid="input-credential-username"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">
                  Category *
                </label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className="w-full bg-white border-2 border-gray-200 p-3 focus:border-primary focus:outline-none text-text-primary"
                  data-testid="select-credential-category"
                >
                  {categories.map((cat) => (
                    <option key={cat.id} value={cat.name}>
                      {cat.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                Password *
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  required
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="w-full bg-white border-2 border-gray-200 p-3 focus:border-primary focus:outline-none text-text-primary font-mono pr-24"
                  placeholder="Enter password"
                  autoComplete="new-password"
                  data-testid="input-credential-password"
                />
                <div className="absolute right-3 top-1/2 -translate-y-1/2 flex gap-2">
                  <button
                    type="button"
                    onClick={generatePassword}
                    className="text-primary hover:text-primary-hover"
                    title="Generate password"
                    data-testid="btn-generate-password"
                  >
                    <Key size={20} />
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="text-text-muted hover:text-text-primary"
                    data-testid="btn-toggle-password-visibility"
                  >
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">
                  URL (Optional)
                </label>
                <input
                  type="text"
                  value={formData.url}
                  onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                  className="w-full bg-white border-2 border-gray-200 p-3 focus:border-primary focus:outline-none text-text-primary"
                  placeholder="https://example.com"
                  data-testid="input-credential-url"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">Project (Optional)</label>
                <select
                  value={formData.project_id}
                  onChange={(e) => setFormData({ ...formData, project_id: e.target.value })}
                  className="w-full bg-white border-2 border-gray-200 rounded-lg p-3 focus:border-primary focus:outline-none text-text-primary"
                  data-testid="select-credential-project"
                >
                  <option value="">No Project</option>
                  {projects.map((p) => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                Notes (Optional)
              </label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={4}
                className="w-full bg-white border-2 border-gray-200 p-3 focus:border-primary focus:outline-none text-text-primary resize-none"
                placeholder="Additional information..."
                data-testid="input-credential-notes"
              />
            </div>

            <div className="flex gap-4 pt-4">
              <button
                type="submit"
                disabled={loading}
                className="btn-primary flex-1"
                data-testid="btn-save-credential"
              >
                {loading ? 'Saving...' : 'Save Credential'}
              </button>
              <button
                type="button"
                onClick={() => navigate('/dashboard')}
                className="btn-outline flex-1"
                data-testid="btn-cancel-add-credential"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}